import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { localCopy } from 'tracked-toolbox';

import styles from './edit-title.module.css';

interface SettingsCurrencyConversionModalsEditTitleSignature {
  Args: {
    activeExchangeRatesProfile: CurrencyConversionExchangeRatesProfile;
    onClose: () => void;
  };
}

export default class SettingsCurrencyConversionModalsEditTitle extends Component<SettingsCurrencyConversionModalsEditTitleSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @localCopy('args.activeExchangeRatesProfile.title') declare tempValue: string;

  updateTitleTask = task(async (): Promise<void> => {
    const title = this.tempValue.trim();

    try {
      await this.currencyConversion.updateProfileTitleTask.perform(
        title,
        this.args.activeExchangeRatesProfile,
      );

      this.toast.success(
        this.intl.t('settings.currency_conversion.changes_saved'),
        {
          showIcon: true,
        },
      );

      this.usageAnalytics.track('Exchange Rate Profile Title Edited', {
        eventTrigger: 'profile-title-edited',
      });
    } catch (error) {
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }

    this.args.onClose();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isConfirmDisabled() {
    return (
      this.tempValue.trim().length === 0 ||
      this.tempValue.trim() === this.args.activeExchangeRatesProfile.title ||
      this.tempValue.length > 30 ||
      this.isUpdating
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUpdating() {
    return this.updateTitleTask.isRunning;
  }

  @action handleUpdateTitle(): void {
    void this.updateTitleTask.perform();
  }

  @action onInputValue(event: Event): void {
    this.tempValue = (event.target as HTMLInputElement).value;
  }

  <template>
    <PrismModal @isCloseDisabled={{this.isUpdating}} @onClose={{@onClose}}>
      <:headerContent>
        <h3 class="flex w-full items-center">
          {{t "settings.currency_conversion.actions.edit_title"}}
        </h3>
      </:headerContent>

      <:default>
        <div class="w-full p-500 {{styles.title-input-container}}">
          <PrismInput
            @autofocus={{true}}
            @maxLength={{30}}
            @onInput={{this.onInputValue}}
            @removePadding={{true}}
            @type="text"
            @value={{this.tempValue}}
          />
        </div>
      </:default>

      <:footer>
        <div class="settings-modal__footer">
          <GeneralFormFieldsCancelAndConfirm
            @cancel={{@onClose}}
            @confirm={{this.handleUpdateTitle}}
            @confirmText={{t "settings.currency_conversion.actions.save_title"}}
            @isCancelDisabled={{this.isUpdating}}
            @isConfirmDisabled={{this.isConfirmDisabled}}
            @isConfirmLoading={{this.isUpdating}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
